import { RECOMMENDED, REQUIRED, t, te } from "../types.ts";
import type {
  ChecklistItem,
  ChecklistSectionConfig,
  TranslatedText,
} from "../types.ts";

function item(
  requirement: TranslatedText,
  required: TranslatedText,
  details: TranslatedText,
  description: TranslatedText = te(""),
): ChecklistItem {
  return { requirement, required, details, description };
}

export const digitalization: ChecklistSectionConfig = {
  id: "digitalization",
  gradient: "from-indigo-700 to-indigo-800",
  hoverBg: "indigo-600",
  title: t("Digitalization", "Digitalizācija", "Digitalizzazione"),
  tagline: t(
    "Reviewing existing digital footprints and moving from manual to structured systems. Focus: Quality, Coherence, Integrity.",
    "Esošo digitālo pēdu pārskatīšana un pāreja no manuālām uz strukturētām sistēmām. Fokuss: Kvalitāte, Koherence, Integritāte.",
    "Revisione delle impronte digitali esistenti e passaggio da sistemi manuali a strutturati. Focus: Qualità, Coerenza, Integrità.",
  ),
  subsections: [
    {
      title: t(
        "2.1. Data Quality",
        "2.1. Datu Kvalitāte",
        "2.1. Qualità dei Dati",
      ),
      items: [
        item(
          t(
            "Input Validation & Quality Assurance",
            "Ievades Validācija un Kvalitātes Nodrošināšana",
            "Validazione degli Input e Garanzia di Qualità",
          ),
          REQUIRED,
          t(
            "Are validation rules in place to prevent bad data entry, and is there a system to review input/output quality?",
            "Vai ir ieviesti validācijas noteikumi, lai novērstu nepareizu datu ievadi, un vai ir sistēma ievades/izvades kvalitātes pārskatīšanai?",
            "Sono in vigore regole di validazione per prevenire l'inserimento di dati errati, ed esiste un sistema per verificare la qualità di input/output?",
          ),
          t(
            "e.g., use dropdowns instead of free text for status fields; run monthly data-quality audits.",
            "piem., izmantojiet nolaižamos sarakstus statusa laukos, nevis brīvo tekstu; veiciet ikmēneša datu kvalitātes auditus.",
            "es., usare menu a discesa invece di testo libero per i campi di stato; eseguire audit mensili sulla qualità dei dati.",
          ),
        ),
        item(
          t(
            "Consistency and Coherence",
            "Konsekvence un Koherence",
            "Coerenza e Uniformità",
          ),
          REQUIRED,
          t(
            "Are naming conventions and formats consistent across platforms? Does the data make sense across the ecosystem?",
            "Vai nosaukumu konvencijas un formāti ir konsekventi visās platformās? Vai dati ir jēgpilni visā ekosistēmā?",
            "Le convenzioni di denominazione e i formati sono coerenti tra le piattaforme? I dati hanno senso in tutto l'ecosistema?",
          ),
          t(
            "e.g., 'Active Client' should imply the same criteria in CRM, billing, and reporting systems.",
            "piem., 'Aktīvs klients' jānozīmē tie paši kritēriji CRM, norēķinu un atskaišu sistēmās.",
            "es., 'Cliente Attivo' dovrebbe implicare gli stessi criteri in CRM, fatturazione e sistemi di reporting.",
          ),
        ),
        item(
          t("Data Lifecycle", "Datu Dzīves Cikls", "Ciclo di Vita dei Dati"),
          REQUIRED,
          t(
            "Are retention, archiving, and deletion rules defined — especially for GDPR / AI training data?",
            "Vai ir definēti glabāšanas, arhivēšanas un dzēšanas noteikumi — īpaši GDPR / MI apmācības datiem?",
            "Sono definite le regole di conservazione, archiviazione ed eliminazione — specialmente per i dati GDPR / addestramento IA?",
          ),
          t(
            "e.g., delete lead records after 2 years of inactivity; archive closed contracts to cold storage.",
            "piem., dzēsiet potenciālo klientu ierakstus pēc 2 gadu neaktivitātes; arhivējiet slēgtos līgumus aukstajā krātuvē.",
            "es., eliminare i record di lead dopo 2 anni di inattività; archiviare i contratti chiusi in cold storage.",
          ),
        ),
      ],
    },
    {
      title: t(
        "2.2. Tool Architecture",
        "2.2. Rīku Arhitektūra",
        "2.2. Architettura degli Strumenti",
      ),
      items: [
        item(
          t(
            "Digitalization Suitability",
            "Digitalizācijas Piemērotība",
            "Idoneità alla Digitalizzazione",
          ),
          RECOMMENDED,
          t(
            "Is the process clearly defined and structured enough to transition into a digital workflow?",
            "Vai process ir pietiekami skaidri definēts un strukturēts, lai pārietu uz digitālo darbplūsmu?",
            "Il processo è sufficientemente chiaro e strutturato per passare a un flusso di lavoro digitale?",
          ),
          t(
            "Ensure you aren't simply digitizing a broken process — standardize first (Phase 1) before digitizing.",
            "Pārliecinieties, ka vienkārši nedigitalizējat nepilnīgu procesu — vispirms standartizējiet (1. fāze) pirms digitalizēšanas.",
            "Assicurarsi di non digitizzare semplicemente un processo difettoso — standardizzare prima (Fase 1) prima di digitalizzare.",
          ),
        ),
        item(
          t(
            "Complexity Assessment",
            "Sarežģītības Novērtējums",
            "Valutazione della Complessità",
          ),
          REQUIRED,
          t(
            "What is the nature of the data? Static/Flat vs Relational?",
            "Kāds ir datu raksturs? Statiski/plakani pret Relāciju?",
            "Qual è la natura dei dati? Statici/Flat vs Relazionali?",
          ),
          t(
            "Static/Flat Data → Google Sheets, Excel; Relational Data → Airtable, SmartSuite, a proper DB.",
            "Statiski/Plakani dati → Google Sheets, Excel; Relāciju dati → Airtable, SmartSuite, īsta DB.",
            "Dati Statici/Flat → Google Sheets, Excel; Dati Relazionali → Airtable, SmartSuite, un DB appropriato.",
          ),
        ),
        item(
          t(
            "System Fit & Lifecycle Risks",
            "Sistēmas Atbilstība un Dzīves Cikla Riski",
            "Adeguatezza del Sistema e Rischi del Ciclo di Vita",
          ),
          REQUIRED,
          t(
            "Are there integration possibilities, exit strategies, and vendor lock-in or tool lifecycle risks?",
            "Vai ir integrācijas iespējas, izejas stratēģijas un piegādātāja atkarības vai rīku dzīves cikla riski?",
            "Esistono possibilità di integrazione, strategie di uscita e rischi di vendor lock-in o del ciclo di vita dello strumento?",
          ),
          t(
            "Evaluate API availability, data export formats, and what happens if the vendor shuts down.",
            "Novērtējiet API pieejamību, datu eksporta formātus un to, kas notiek, ja piegādātājs pārtrauc darbību.",
            "Valutare la disponibilità delle API, i formati di esportazione dei dati e cosa succede se il fornitore chiude.",
          ),
        ),
        item(
          t("Ergonomics", "Ergonomika", "Ergonomia"),
          RECOMMENDED,
          t(
            "How effective is the solution across UX (internal/external), DX (development), and cost?",
            "Cik efektīvs ir risinājums UX (iekšējo/ārējo), DX (izstrādes) un izmaksu ziņā?",
            "Quanto è efficace la soluzione in termini di UX (interna/esterna), DX (sviluppo) e costo?",
          ),
          t(
            "UX: end-user adoption rate; DX: time to onboard a new developer; $X: TCO vs manual cost saved.",
            "UX: gala lietotāja pielāgošanās rādītājs; DX: laiks jauna izstrādātāja adaptācijai; $X: TCO salīdzinājumā ar ietaupītajām manuālajām izmaksām.",
            "UX: tasso di adozione degli utenti finali; DX: tempo per inserire un nuovo sviluppatore; $X: TCO vs costo manuale risparmiato.",
          ),
        ),
      ],
    },
    {
      title: t(
        "2.3. Integrity & Trust",
        "2.3. Integritāte un Uzticamība",
        "2.3. Integrità e Fiducia",
      ),
      items: [
        item(
          t(
            "Single Source of Truth",
            "Vienots Patiesības Avots",
            "Unica Fonte di Verità",
          ),
          REQUIRED,
          t(
            "Is there a defined authoritative primary system of record for critical data?",
            "Vai ir definēta autoritatīva primārā uzskaites sistēma kritiskajiem datiem?",
            "Esiste un sistema di registrazione primario autorevole definito per i dati critici?",
          ),
          t(
            "Designate one system as the master (e.g., CRM for customer data) and sync all others from it.",
            "Norādiet vienu sistēmu kā galveno (piem., CRM klientu datiem) un sinhronizējiet visas pārējās no tās.",
            "Designare un sistema come master (es., CRM per i dati dei clienti) e sincronizzare tutti gli altri da esso.",
          ),
        ),
        item(
          t(
            "Access & Permissions Control",
            "Piekļuves un Atļauju Kontrole",
            "Controllo degli Accessi e delle Autorizzazioni",
          ),
          REQUIRED,
          t(
            "Are permissions restricted? Is MFA/SSO required for sensitive systems?",
            "Vai atļaujas ir ierobežotas? Vai konfidenciālām sistēmām ir nepieciešams MFA/SSO?",
            "Le autorizzazioni sono limitate? È richiesto MFA/SSO per i sistemi sensibili?",
          ),
          t(
            "Apply least-privilege access; enforce MFA on admin accounts; audit permissions quarterly.",
            "Piemērojiet mazākās privilēģijas piekļuvi; ieviesiet MFA administratoru kontos; auditējiet atļaujas reizi ceturksnī.",
            "Applicare l'accesso con privilegi minimi; imporre MFA sugli account amministrativi; verificare le autorizzazioni trimestralmente.",
          ),
        ),
        item(
          t(
            "Encryption & Residency",
            "Šifrēšana un Datu Atrašanās Vieta",
            "Crittografia e Residenza dei Dati",
          ),
          REQUIRED,
          t(
            "Is data encrypted at rest/in transit? Does the data location comply with local laws?",
            "Vai dati ir šifrēti miera stāvoklī/tranzītā? Vai datu atrašanās vieta atbilst vietējiem likumiem?",
            "I dati sono crittografati a riposo/in transito? La posizione dei dati è conforme alle leggi locali?",
          ),
          t(
            "e.g., EU servers for GDPR; TLS 1.2+ in transit; AES-256 at rest for PII.",
            "piem., ES serveri GDPR ievērošanai; TLS 1.2+ tranzītā; AES-256 miera stāvoklī PII datiem.",
            "es., server UE per GDPR; TLS 1.2+ in transito; AES-256 a riposo per i dati personali.",
          ),
        ),
        item(
          t("Audit Logs", "Audita Žurnāli", "Log di Audit"),
          RECOMMENDED,
          t(
            "Does the system record who changed what and when?",
            "Vai sistēma reģistrē, kurš ko mainīja un kad?",
            "Il sistema registra chi ha modificato cosa e quando?",
          ),
          t(
            "Enable audit trails in your SaaS tools; export logs to an immutable store for compliance.",
            "Iespējojiet audita pierakstus SaaS rīkos; eksportējiet žurnālus uz nemainīgu krātuvi atbilstības nodrošināšanai.",
            "Abilitare i trail di audit negli strumenti SaaS; esportare i log in un archivio immutabile per la conformità.",
          ),
        ),
      ],
    },
  ],
};
